
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClassesHeader } from "@/components/classes/ClassesHeader";
import { AddCourseDialog } from "@/components/courses/AddCourseDialog";
import { CoursesList } from "@/components/courses/CoursesList";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useUserStore } from '@/store/userStore';
import { useDraftResumeToast } from "@/hooks/useDraftResumeToast";

const Classes = () => {
  const { t } = useTranslation(['courses', 'appui']);
  const gymId = useUserStore((state) => state.gymId);
  const [courses, setCourses] = useState([]);
  const [isAddClassOpen, setIsAddClassOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState(null);

  // Création de cours interrompue par un reload → proposer de la reprendre.
  useDraftResumeToast([
    {
      formId: "course-form:new",
      message: t("classes.unsavedDraft", { ns: "appui" }),
      description: (d) => (d.name as string) || undefined,
      isEmpty: (d) => !d.name,
      onResume: () => {
        setEditingCourse(null);
        setIsAddClassOpen(true);
      },
    },
  ]);

  // gymId en dépendance : avec [] la liste restait celle de la salle
  // précédente après un changement de salle.
  useEffect(() => {
    fetchCourses();
  }, [gymId]);

  const fetchCourses = async () => {
    if (!gymId) {
      setCourses([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const { getCourses } = await import('@/services/edgeFunctionWrappers/coursesManagementWrapper');
      const data = await getCourses(gymId);
      setCourses(data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error(t('fetchCoursesError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCourse = async (courseData: any) => {
    const isUpdate = !!courseData.id;
    if (!gymId) {
      toast.error(t('fetchCoursesError'));
      return false;
    }
    try {
      const { updateCourse, createCourse } = await import('@/services/edgeFunctionWrappers/coursesManagementWrapper');

      const calendarIds: string[] | undefined = Array.isArray(courseData.calendar_ids)
        ? courseData.calendar_ids
        : undefined;

      if (isUpdate) {
        await updateCourse(courseData.id, {
          name: courseData.name,
          description: courseData.description,
          category_id: courseData.category_id,
          default_capacity: parseInt(courseData.default_capacity),
          default_duration: parseInt(courseData.default_duration)
        }, gymId, calendarIds);

        toast.success(t('courseUpdatedSuccess'));
      } else {
        await createCourse({
          name: courseData.name,
          description: courseData.description,
          category_id: courseData.category_id,
          default_capacity: parseInt(courseData.default_capacity),
          default_duration: parseInt(courseData.default_duration),
          calendar_ids: calendarIds,
        });

        toast.success(t('courseAddedSuccess'));
      }
      
      fetchCourses();
      setEditingCourse(null);
      return true;
    } catch (error: any) {
      console.error('Error saving course:', error, error?.message);
      const detail = error?.message ? ` (${error.message})` : '';
      toast.error((isUpdate ? t('courseUpdateError') : t('courseAddError')) + detail);
      return false;
    }
  };
  
  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setIsAddClassOpen(true);
  };
  
  const handleDeleteCourse = async (courseId) => {
    if (!gymId) return;
    try {
      const { deleteCourse } = await import('@/services/edgeFunctionWrappers/coursesManagementWrapper');

      await deleteCourse(courseId, gymId);
      toast.success(t('courseDeletedSuccess'));
      fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error(t('courseDeleteError'));
    }
  };

  return (
    <div className="min-h-screen flex flex-col w-full">
      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full animate-fade-in">
        <ClassesHeader onAddClass={() => {
          setEditingCourse(null);
          setIsAddClassOpen(true);
        }} />
        
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <p className="text-muted-foreground">Loading courses...</p>
          </div>
        ) : (
          <CoursesList
            courses={courses}
            onEdit={handleEditCourse}
            onDelete={handleDeleteCourse}
          />
        )}
      </main>
      
      <AddCourseDialog
        open={isAddClassOpen}
        onOpenChange={(open) => {
          setIsAddClassOpen(open);
          if (!open) setEditingCourse(null);
        }}
        onSave={handleAddCourse}
        editingCourse={editingCourse}
      />
    </div>
  );
};

export default Classes;
